const ALLOWED_TAGS = new Set([
  'a', 'blockquote', 'br', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr',
  'li', 'ol', 'p', 's', 'span', 'strong', 'table', 'tbody', 'td', 'tfoot',
  'th', 'thead', 'tr', 'u', 'ul',
]);

const BLOCKED_TAGS = new Set([
  'audio', 'button', 'canvas', 'embed', 'form', 'iframe', 'img', 'input',
  'link', 'math', 'meta', 'object', 'script', 'select', 'style', 'svg',
  'textarea', 'video',
]);

const escapeHtml = (value) => String(value || '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const sanitizeHref = (href) => {
  const value = String(href || '').trim();
  if (!value) return '';
  const normalized = Array.from(value)
    .filter((character) => character.charCodeAt(0) > 32)
    .join('')
    .toLowerCase();
  if (
    normalized.startsWith('https://') ||
    normalized.startsWith('http://') ||
    normalized.startsWith('mailto:') ||
    normalized.startsWith('tel:') ||
    normalized.startsWith('/') ||
    normalized.startsWith('#')
  ) {
    return value;
  }
  return '';
};

const sanitizeStyle = (styleText) => {
  const allowed = [];
  String(styleText || '').split(';').forEach((declaration) => {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex < 0) return;
    const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
    const value = declaration.slice(separatorIndex + 1).trim().toLowerCase();
    if (property === 'text-align' && /^(left|right|center|justify)$/.test(value)) {
      allowed.push(`${property}:${value}`);
    }
    if (property === 'font-weight' && /^(bold|[5-9]00)$/.test(value)) {
      allowed.push('font-weight:bold');
    }
    if (property === 'font-style' && value === 'italic') {
      allowed.push('font-style:italic');
    }
    if (property === 'text-decoration' && /^(underline|line-through)$/.test(value)) {
      allowed.push(`${property}:${value}`);
    }
  });
  return allowed.join(';');
};

const sanitizeNode = (node, outputDocument) => {
  if (node.nodeType === 3) return outputDocument.createTextNode(node.textContent || '');
  const fragment = outputDocument.createDocumentFragment();
  if (node.nodeType !== 1) return fragment;

  const tagName = node.tagName.toLowerCase();
  if (BLOCKED_TAGS.has(tagName)) return fragment;
  if (!ALLOWED_TAGS.has(tagName)) {
    Array.from(node.childNodes).forEach((child) => {
      fragment.appendChild(sanitizeNode(child, outputDocument));
    });
    return fragment;
  }

  const element = outputDocument.createElement(tagName === 'h1' ? 'h2' : tagName);
  const style = sanitizeStyle(node.getAttribute('style'));
  if (style) element.setAttribute('style', style);

  if (tagName === 'a') {
    const href = sanitizeHref(node.getAttribute('href'));
    if (href) {
      element.setAttribute('href', href);
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener noreferrer');
    }
  }
  if (tagName === 'td' || tagName === 'th') {
    ['colspan', 'rowspan'].forEach((attribute) => {
      const value = node.getAttribute(attribute);
      if (/^(?:[1-9]|1[0-9]|20)$/.test(value || '')) {
        element.setAttribute(attribute, value);
      }
    });
  }

  Array.from(node.childNodes).forEach((child) => {
    element.appendChild(sanitizeNode(child, outputDocument));
  });
  return element;
};

export const isFormattedLegalContent = (content) =>
  /<(?:p|div|h[2-6]|ul|ol|li|table|blockquote|strong|em|br)\b/i.test(String(content || ''));

export const plainTextToLegalHtml = (content) => {
  const blocks = String(content || '').trim().split(/\r?\n\s*\r?\n/).filter(Boolean);
  return blocks.map((block) => {
    const heading = block.match(/^(#{1,5})\s*(.+)$/s);
    if (heading) {
      const level = Math.min(heading[1].length + 1, 6);
      return `<h${level}>${escapeHtml(heading[2].trim())}</h${level}>`;
    }
    return `<p>${escapeHtml(block).replace(/\r?\n/g, '<br>')}</p>`;
  }).join('');
};

export const legalContentToHtml = (content) => (
  isFormattedLegalContent(content) ? sanitizeLegalHtml(content) : plainTextToLegalHtml(content)
);

export function sanitizeLegalHtml(html) {
  if (typeof DOMParser === 'undefined' || typeof document === 'undefined') {
    return plainTextToLegalHtml(String(html || '').replace(/<[^>]*>/g, ''));
  }
  const source = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const output = document.implementation.createHTMLDocument('');
  const container = output.createElement('div');
  Array.from(source.body.childNodes).forEach((node) => {
    container.appendChild(sanitizeNode(node, output));
  });
  return container.innerHTML;
}
